/**
 * @fileoverview Emlakjet ChatGPT Widget - Ana Uygulama Bileseni
 *
 * Bu dosya, ChatGPT icinde render edilen Emlakjet widget'inin ana React
 * bilesenini icerir. Coordinator hook'lari kullanarak ilan arama, harita
 * gorunumu, filtre yonetimi ve ilan detayi islevlerini koordine eder.
 *
 * @module widget/App
 * @see {@link https://platform.openai.com/docs/guides/apps-sdk} OpenAI Apps SDK
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useWidgetProps } from "../use-widget-props";

import { openExternal, subscribeDebugLogs } from "./utils";

import {
  FullscreenLayout,
  ListingsView,
  ProjectsView,
  EmptyState,
  ErrorRecovery,
  CompareFloatingBar,
  CompareView,
} from "./components";

import {
  useAppState,
  useFilterHandlers,
  useSavedListings,
  useSearchCoordinator,
  useDetailCoordinator,
  useMapCoordinator,
  useErrorRecovery,
  useCollection,
} from "./hooks";

import { LIMITS } from "./utils";

/**
 * Ana Widget Bileseni
 *
 * ChatGPT tool cagrisi sonrasinda render edilen ana React bileseni.
 * Coordinator hook'lari kullanarak tum alt bilesenleri koordine eder.
 *
 * @component
 * @returns {JSX.Element} Widget UI
 */
function App() {
  // ==================== Core App State ====================
  const {
    isDark,
    isFullscreen,
    requestFullscreen,
    exitFullscreen,
    widgetState,
    updateWidgetState,
    setRegionPriceInfo,
    regionAveragePrice,
  } = useAppState();

  // ==================== Host Output (from ChatGPT) ====================
  const hostOutput = useWidgetProps({
    kind: null,
    total: 0,
    page: 1,
    query: { route: null, path: null, filters: null },
    listings: [],
    projects: [],
    companyName: null,
  });

  // ==================== Search Coordinator ====================
  const {
    output,
    setOutput,
    callError,
    setCallError,
    isLoadingMore,
    isSorting,
    currentSort,
    callSearch,
    handleSortChange,
    handleLoadMore,
  } = useSearchCoordinator({
    widgetState,
    updateWidgetState,
    setRegionPriceInfo,
    hostOutput,
  });

  // ==================== Extract Output Values ====================
  const total = Number(output?.total ?? 0);
  const page = Number(output?.page ?? 1);
  const query = output?.query ?? { route: null, path: null, filters: null };
  const listings = Array.isArray(output?.listings) ? output.listings : [];
  const projects = Array.isArray(output?.projects) ? output.projects : [];
  const showProjects = projects.length > 0;
  const showListings = listings.length > 0;

  // Debug: Build IDs - cache sorunu tespiti için
  const serverBuild = hostOutput?.buildId ?? null;
  // eslint-disable-next-line no-undef
  const widgetBuild = typeof __WIDGET_BUILD_ID__ !== "undefined" ? __WIDGET_BUILD_ID__ : null;

  // Debug logs state - subscribe to updates
  const [debugLogs, setDebugLogs] = useState([]);
  useEffect(() => {
    return subscribeDebugLogs(setDebugLogs);
  }, []);

  // ==================== Detail Coordinator ====================
  const {
    selectedListingId,
    listingDetail,
    detailLoading,
    selectedImageIndex,
    setSelectedImageIndex,
    handleDetailClick,
    handleCloseDetail,
  } = useDetailCoordinator({
    updateWidgetState,
    requestFullscreen,
    widgetState,
    query,
    setCallError,
  });

  // ==================== Map Coordinator ====================
  const {
    isMapSearching,
    mapSearchEmpty,
    handleMapSearchInArea: baseMapSearchInArea,
  } = useMapCoordinator({
    setOutput,
    setCallError,
    handleCloseDetail,
  });

  // Wrap map search to pass query
  const handleMapSearchInArea = useCallback((bounds) => {
    baseMapSearchInArea(bounds, query);
  }, [baseMapSearchInArea, query]);

  // ==================== Error Recovery ====================
  const {
    handleRetrySearch,
    handleRemoveFiltersAndSearch,
    handleExpandLocation,
  } = useErrorRecovery({
    callSearch,
    setCallError,
    query,
    currentSort,
  });

  // ==================== Filter Handlers ====================
  const [pathDraft, setPathDraft] = useState(query?.path ?? "");

  // Sync pathDraft with query.path
  useEffect(() => {
    setPathDraft(query?.path ?? "");
  }, [query?.path]);

  const { queryFilters, handleRemoveFilter, handleApplyFilter } = useFilterHandlers({
    query,
    pathDraft,
    callSearch,
  });

  // ==================== Saved Listings ====================
  const {
    savedListingIdSet,
    toggleSaveListing,
    isAtLimit: isSaveAtLimit,
  } = useSavedListings(
    widgetState?.modelContent?.savedListingIds || [],
    widgetState?.modelContent?.savedProjectIds || [],
    updateWidgetState
  );

  // ==================== Compare State ====================
  const [showCompareView, setShowCompareView] = useState(false);

  const {
    items: compareListings,
    itemIdSet: compareListingIdSet,
    isAtLimit: isCompareAtLimit,
    toggle: toggleCompare,
    remove: removeFromCompare,
    clear: clearCompareCollection,
  } = useCollection({ maxItems: LIMITS.MAX_COMPARE });

  const clearCompare = useCallback(() => {
    clearCompareCollection();
    setShowCompareView(false);
  }, [clearCompareCollection]);

  const openCompareView = useCallback(() => {
    if (compareListings.length >= 2) {
      setShowCompareView(true);
    }
  }, [compareListings.length]);

  // ==================== Memoized Props ====================
  const noopAddFilter = useCallback(() => {}, []);

  const searchProps = useMemo(() => ({
    listings,
    filters: queryFilters,
    total,
    onRemoveFilter: handleRemoveFilter,
    onApplyFilter: handleApplyFilter,
    onAddFilter: noopAddFilter,
  }), [listings, queryFilters, total, handleRemoveFilter, handleApplyFilter, noopAddFilter]);

  const detailProps = useMemo(() => ({
    selectedListingId,
    listingDetail,
    detailLoading,
    regionAveragePrice,
    selectedImageIndex,
    setSelectedImageIndex,
    onCloseDetail: handleCloseDetail,
    onDetailClick: handleDetailClick,
  }), [selectedListingId, listingDetail, detailLoading, regionAveragePrice, selectedImageIndex, setSelectedImageIndex, handleCloseDetail, handleDetailClick]);

  const mapProps = useMemo(() => ({
    onMapSearchInArea: handleMapSearchInArea,
    isMapSearching,
    mapSearchEmpty,
  }), [handleMapSearchInArea, isMapSearching, mapSearchEmpty]);

  // ==================== Fullscreen Mode Render ====================
  if (isFullscreen && (showListings || mapSearchEmpty)) {
    return (
      <div className="relative w-full h-full">
        {/* Debug: Build ID + Logs Banner - fullscreen (disabled)
        {(serverBuild || widgetBuild) && (
          <div className={`absolute top-0 left-0 right-0 z-50 text-[10px] px-2 py-0.5 font-mono ${isDark ? "bg-zinc-800/90 text-zinc-500" : "bg-gray-100/90 text-gray-400"}`}>
            <div>S={serverBuild?.slice(0, 19) ?? "?"} | W={widgetBuild?.slice(0, 19) ?? "?"}</div>
            {debugLogs.length > 0 && (
              <div className="mt-0.5 border-t border-current/20 pt-0.5">
                {debugLogs.slice(-3).map((log, i) => (
                  <div key={i}>{log}</div>
                ))}
              </div>
            )}
          </div>
        )}
        */}
        <FullscreenLayout
          {...searchProps}
          {...detailProps}
          {...mapProps}
          isDark={isDark}
          onExitFullscreen={exitFullscreen}
          openExternal={openExternal}
          savedListingIdSet={savedListingIdSet}
          onToggleSave={toggleSaveListing}
          isSaveAtLimit={isSaveAtLimit}
          compareListingIdSet={compareListingIdSet}
          onToggleCompare={toggleCompare}
          isCompareAtLimit={isCompareAtLimit}
        />
      </div>
    );
  }

  // ==================== Inline Mode Render ====================
  return (
    <div
      className={`antialiased relative w-full rounded-2xl border overflow-hidden ${
        isDark
          ? "bg-zinc-900 border-white/10 text-white"
          : "bg-white border-black/10 text-black"
      }`}
    >
      {/* Debug: Build ID + Logs Banner (disabled)
      {(serverBuild || widgetBuild) && (
        <div className={`text-[10px] px-2 py-0.5 font-mono ${isDark ? "bg-zinc-800 text-zinc-500" : "bg-gray-100 text-gray-400"}`}>
          <div>S={serverBuild?.slice(0, 19) ?? "?"} | W={widgetBuild?.slice(0, 19) ?? "?"}</div>
          {debugLogs.length > 0 && (
            <div className="mt-0.5 border-t border-current/20 pt-0.5">
              {debugLogs.slice(-3).map((log, i) => (
                <div key={i}>{log}</div>
              ))}
            </div>
          )}
        </div>
      )}
      */}

      {callError && (
        <ErrorRecovery
          error={callError}
          query={query}
          isDark={isDark}
          onDismiss={() => setCallError(null)}
          onRetry={handleRetrySearch}
          onRemoveFilters={handleRemoveFiltersAndSearch}
          onExpandLocation={handleExpandLocation}
        />
      )}

      {showProjects ? (
        <ProjectsView projects={projects} isDark={isDark} />
      ) : showListings ? (
        <ListingsView
          listings={listings}
          total={total}
          page={page}
          query={query}
          filters={queryFilters}
          isDark={isDark}
          isLoadingMore={isLoadingMore}
          onRemoveFilter={handleRemoveFilter}
          onApplyFilter={handleApplyFilter}
          onDetailClick={handleDetailClick}
          onLoadMore={handleLoadMore}
          onRequestFullscreen={requestFullscreen}
          currentSort={currentSort}
          onSortChange={handleSortChange}
          isSorting={isSorting}
          savedListingIdSet={savedListingIdSet}
          onToggleSave={toggleSaveListing}
          isSaveAtLimit={isSaveAtLimit}
          compareListingIdSet={compareListingIdSet}
          onToggleCompare={toggleCompare}
          isCompareAtLimit={isCompareAtLimit}
        />
      ) : (
        <EmptyState isDark={isDark} />
      )}

      {compareListings.length > 0 && !showCompareView && (
        <CompareFloatingBar
          listings={compareListings}
          onRemove={removeFromCompare}
          onCompare={openCompareView}
          onClear={clearCompare}
          isDark={isDark}
        />
      )}

      {showCompareView && (
        <CompareView
          listings={compareListings}
          isDark={isDark}
          onClose={() => setShowCompareView(false)}
          onRemove={removeFromCompare}
        />
      )}
    </div>
  );
}

export { App };
export default App;
